import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useProduct } from '@thd-nucleus/data-sources';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import PriceSimple from './partials/PriceSimple';
import PriceDetailed from './partials/PriceDetailed';
import Sku from '../models/Sku';
import helpers, { PRICE_TYPES, PRODUCT_DOMAINS } from '../helpers/price-helper';

export const getDisplayedPrice = (product) => {

  const priceObj = {
    product
  };
  const { _price, type } = helpers.getPricingObject(priceObj);
  const {
    price = '0',
    wasPrice,
    alternatePrice,
    mapPrice,
    pricingMessage
  } = _price;
  let retPrice = '0';

  switch (type) {
    case PRICE_TYPES.STANDARD:
    case PRICE_TYPES.STARTING_AT:
    case PRICE_TYPES.NO_DETAILS:
      return price;

    case PRICE_TYPES.STRIKE_THROUGH:
      return wasPrice.special;

    case PRICE_TYPES.ECO_REBATE:
      if (mapPrice || pricingMessage) {
        return '0';
      }
      if (!mapPrice) {
        retPrice = wasPrice ? wasPrice.special : price;
        return retPrice;
      }
      return '0';

    case PRICE_TYPES.INSTANT_REBATE:
    case PRICE_TYPES.BULK_PRICE:
    case PRICE_TYPES.LIMIT_PER_ORDER:
      retPrice = wasPrice ? wasPrice.special : price;
      return retPrice;

    case PRICE_TYPES.UNIT_AND_CASE:
      return alternatePrice;

    case PRICE_TYPES.MAP_PRICE:
    case PRICE_TYPES.BRIO:
    case PRICE_TYPES.LIVE_GOODS:
      return '0';

    case PRICE_TYPES.RANGE_PRICE:
      return price;

    default: return '0';
  }
};

const Price = (props) => {
  const { channel, store, clientStore } = useContext(ExperienceContext);
  const { storeId: defaultStoreId, storeZip: defaultStoreZip } = useStore();
  const {
    disableRangePricing,
    itemId,
    storeId = defaultStoreId,
    zipCode = defaultStoreZip,
    large,
    omitDetails,
    basic,
    displayEachUom,
    hideBadge,
    onSavingsCenterToggle,
    /* eslint-disable-next-line react/prop-types */
    product,
    skip
  } = props;

  const { data, loading, error } = useProduct({
    itemId,
    domains: PRODUCT_DOMAINS,
    storeId,
    skip: !!product,
    ssr: !skip
  });

  if (skip && !data) return null;

  if (loading) {
    return (
      <LoadingPlaceholder
        type="text"
        ready={false}
        color="#E0E0E0"
        rows={1}
        showLoadingAnimation
      >
        Loading...
      </LoadingPlaceholder>
    );
  }
  if (error && !data) {
    return <pre><code>{error.toString()}</code></pre>;
  }
  if (!data && !product) return null;

  const sku = product
    ? new Sku(product)
    : new Sku(data?.model?.product || {});

  return (
    <>
      {large && (
        <PriceDetailed
          channel={channel}
          disableRangePricing={disableRangePricing}
          product={sku}
          omitDetails={omitDetails}
          displayEachUom={displayEachUom}
          zipCode={zipCode}
          hideBadge={hideBadge}
          storeId={storeId}
        />
      )}
      {!large && (
        <PriceSimple
          channel={channel}
          disableRangePricing={disableRangePricing}
          product={sku}
          omitDetails={omitDetails}
          basic={basic}
          displayEachUom={displayEachUom}
          zipCode={zipCode}
          onSavingsCenterToggle={onSavingsCenterToggle}
          hideBadge={hideBadge}
        />
      )}
    </>
  );
};

Price.propTypes = {
  basic: PropTypes.bool,
  disableRangePricing: PropTypes.bool,
  displayEachUom: PropTypes.bool,
  hideBadge: PropTypes.bool,
  large: PropTypes.bool,
  omitDetails: PropTypes.bool,
  onSavingsCenterToggle: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  skip: PropTypes.bool,
  storeId: PropTypes.string,
  zipCode: PropTypes.string
};

Price.defaultProps = {
  basic: false,
  disableRangePricing: false,
  displayEachUom: false,
  hideBadge: false,
  large: true,
  omitDetails: false,
  onSavingsCenterToggle: () => {},
  skip: false,
  storeId: undefined,
  zipCode: undefined
};

export default Price;
